import { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import './SolutionAdvisor.scss';
import countryList from '../services/currencies.json';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { LangDefaults, useLang } from 'src/common/hooks/widgets/useLang';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import Welcome from './Templates/Welcome';
import Assessments from './Templates/Assessments';
import { SolutionAdvisorProps, Step5Props } from '../lib/interfaces';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { FormModal } from 'src/common/utils/FormModal';
import {
  COOKIE_ID,
  fileName,
  FORM_ID,
  INSTANCE_ID,
  location,
  pageUrl,
  segmentsTrack,
  UT30N9J,
  validLocales,
} from '../constants/default';
import { PDFGenerate } from './Templates/Pdf/PDFGenerate';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import Cookies from 'js-cookie';
import { useSolutionAdvisorSelector } from '../lib/redux/hooks';
import { createUseTranslation } from 'src/common/hooks/widgets/useTranslation';
import messages from '../locales/messages';
import { shortcode, title } from '../SolutionAdvisor.widget';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { postMailSubmission } from '../lib/helpers';

const SolutionAdvisor = ({
  langCode,
  countryToTest,
  test,
  isActiveStep, // If it is true, then Step 4 in SDP will be disabled; otherwise, Step 4 will be displayed.
}: SolutionAdvisorProps) => {
  const INSTANCE_ID = 'SA_urx-form';
  const [environment] = useEnv();
  window.urxEnvironment = environment;
  const AEMMessages = useSolutionAdvisorSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);
  const swapLocaleCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [localeCode, updateLocaleCode] = useState(swapLocaleCode.toString());
  const [urxlang] = useState(localeCode.toString());
  const [, setCurrency] = useState('');
  const [checkStep, setCcheckStep] = useState(isActiveStep);
  const [, setCountry] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);
  const inputRef = useRef(null);
  const cookieURL = useCookiesURL(shortcode);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;

  const [open, setOpen] = useState(false);
  const [sendMail, onSendMail] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { bpName, companyId, logo } = useBPName(UT30N9J, true);
  const [pdfObj, setPDFObj] = useState<Step5Props>({ useTranslation });
  const pdfObject = PDFGenerate(pdfObj);
  const [formID, setFormID] = useState('');
  const [formObj, setFormObj] = useState({
    clientName: '',
    widgetName: title,
    pageUrl: window.location.href,
    country: '',
    state: '',
    email: '',
    phoneNumber: '',
    company: '',
    preferredBp: '',
    additionalInfo: '',
    relationship: '',
    widgetMail: true,
    widgetInfoNotes: '',
  });
  window.onUrxFormSubmitSuccessMultiple =
    window.onUrxFormSubmitSuccessMultiple || {};
  window.onUrxFormSubmitSuccessMultiple[INSTANCE_ID] = function (param) {
    if (drawerOpen) {
      const element: HTMLElement = document.getElementsByClassName(
        'downloadLink',
      )[0] as HTMLElement;
      element.click();
    }
    setOpen(false);
    setFormObj((preState) => ({
      ...preState,
      company: param.formValues.company,
    }));

    setDrawerOpen(false);
    onSendMail(true);
  };

  useEffect(() => {
    setlocalCurrency(userLocale);
  }, [userLocale, localeCode, localCurrency]);

  useEffect(() => {
    // for testing purpose only, if ddo_locale exists.
    const ddoLocaleRetrieved =
      new URLSearchParams(window.location.search).get('ddo_locale') || '';
    if (validLocales.includes(ddoLocaleRetrieved)) {
      updateLocaleCode(ddoLocaleRetrieved as IBMLocale);
    }
    const ddoInactive =
      new URLSearchParams(window.location.search).get('ddo_inactive') || false;
    if ([true, 'true'].includes(ddoInactive)) setCcheckStep(true);
  }, []);

  useEffect(() => {
    if (drawerOpen) {
      setTimeout(() => {
        const div = document.getElementsByClassName(
          'form-heading',
        )[0] as HTMLDivElement;
        if (div) {
          div.innerText = useTranslation('btn_download_pdf', true) as string;
        }
      }, 1000);
    }
  });

  useEffect(() => setCookie(), [pdfObj, formID]);

  useEffect(() => {
    if (sendMail) {
      const firstName = document.getElementById(
        'firstName',
      ) as HTMLInputElement;
      const lastName = document.getElementById('lastName') as HTMLInputElement;
      const country = document.getElementById('country') as HTMLInputElement;
      const email = document.getElementById('email') as HTMLInputElement;
      const preferredBp = document.getElementById(
        'Q_BPNAME',
      ) as HTMLInputElement;
      const additionalInfo = document.getElementById(
        'Q_HELP',
      ) as HTMLInputElement;
      const relationship = document.getElementById(
        'Q_RELATIONSHIP_value',
      ) as HTMLInputElement;
      const phoneNumber = document.getElementsByName('phone');
      const post: any = {};
      if (phoneNumber.length > 0) {
        const phoneInput = phoneNumber[0] as HTMLInputElement;
        post['phoneNumber'] = phoneInput?.value;
      }
      if (firstName || lastName) {
        post['clientName'] = `${firstName?.value || ''} ${
          lastName?.value || ''
        }`;
      }
      setTimeout(() => {
        postMailSubmission({
          ...formObj,
          ...post,
          email: email?.value || '',
          country: country?.value || '',
          additionalInfo: additionalInfo?.value || '',
          relationship: relationship?.title || '',
          preferredBp: preferredBp?.value || '',
        });
        onSendMail(false);
      }, 250);
    }
  }, [sendMail]);

  const setCookie = () => {
    const { productObj } = pdfObj;
    const Q_NOTESDATA =
      cookieURL +
      ' | Application Name: ' +
      (useTranslation('modal_header', true) as string) +
      ' | Configuration Title: ' +
      (productObj && productObj?.offname) +
      ' | Configuration Price: ' +
      (productObj && productObj?.offprice);

    //updating form object for email functionality
    setFormObj((preState) => ({
      ...preState,
      widgetInfoNotes: Q_NOTESDATA,
    }));
    //setting cookies
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: formID,
        dcq: {
          Q_NOTESDATA,
          Q_ASSET_URL: window.location.href,
          Q_ASSET_NAME: useTranslation('widget_title', true) as string,
        },
      }),
    );
    console.log(Cookies.get(COOKIE_ID));
  };

  const handleLaunchModal = () => {
    setOpen(true);
    segmentsTrack('Advisor Launch', 'Welcome Page');
  };

  const handleCloseModal = () => {
    setOpen(false);
    setDrawerOpen(false);
  };

  const handleDownloadPDF = async () => {
    initUrxForm(
      environment,
      INSTANCE_ID,
      FORM_ID,
      urxlang as IBMLocale,
      companyId,
      shortcode,
      setFormID,
    );
    setCookie();
    setDrawerOpen(true);
    setOpen(false);
  };

  const propAssistant = {
    test,
    open,
    localeCode,
    isActiveStep: checkStep,
    updateLocaleCode,
    useTranslation,
    handleCloseModal,
    handleDownloadPDF,
    setPDFObj,
  };

  const propsForm = {
    pageTitle: useTranslation('widget_title', true) as string,
    object: useTranslation('widget_object', true) as string,
    productTitle: useTranslation('widget_title', true) as string,
    pageUrl,
    location,
    bpName,
    logo,
    drawerOpen,
    setDrawerOpen,
    formId: formID,
    id: INSTANCE_ID,
    lang,
  };

  return (
    <div className="widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      <div id="grid" className="solution-advisory">
        <Welcome
          handleLaunchModal={handleLaunchModal}
          useTranslation={useTranslation}
        />
        {open && !drawerOpen && <Assessments {...propAssistant} />}
        {drawerOpen && (
          <PDFDownloadLink
            document={pdfObject}
            fileName={fileName()}
            className="link downloadLink"
          />
        )}
        <div ref={inputRef}>
          <FormModal {...propsForm} />
        </div>
      </div>
    </div>
  );
};

export default SolutionAdvisor;
